import React, { useState, useRef, useEffect } from 'react';
import { getCitationUrl } from '@/utils/jsonTyping';
import { InsightItemDisplay, parseInsightsFromJson } from '@/components/InsightItemDisplay';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, CarouselApi } from '@/components/ui/carousel';
import { Copy, Heart, Share, Eye, EyeOff, Star, Clock, Instagram, TrendingUp, CheckCircle, Bookmark, ExternalLink, Check, Sparkles, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Trash2, Undo, MoreVertical, FileEdit, MonitorPlay, Lightbulb } from 'lucide-react';
import { ContentIdea, ViralScoreLevel } from '@/types/dashboard';
import { useFavorites } from '@/hooks/useFavorites';
import { useMarkAsUsed } from '@/hooks/useMarkAsUsed';
import { useIsMobile } from '@/hooks/use-mobile';
interface ContentCardProps {
  idea: ContentIdea;
  onCopyScript: (content: string) => void;
  onFavoriteToggle?: () => void;
  className?: string;
  isRowLayout?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  isTrashView?: boolean;
  onTrashAction?: (ideaId: string, source: 'scraped' | 'user_generated') => void;
}
export const ContentCard = ({
  idea,
  onCopyScript,
  onFavoriteToggle,
  className,
  isRowLayout = false,
  isExpanded: isExpandedProp,
  onToggleExpand,
  isTrashView = false,
  onTrashAction
}: ContentCardProps) => {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isMobile = useIsMobile();

  // Determine the table for the script based on source
  const getTableName = (): 'content_ideas' | 'user_generated_content' => {
    return idea.source === 'user_generated' ? 'user_generated_content' : 'content_ideas';
  };

  const handleNavigateToEditor = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDropdownOpen(false);
    navigate(`/edytor?id=${idea.id}&table=${getTableName()}`);
  };

  const handleNavigateToPrompter = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDropdownOpen(false);
    navigate(`/prompter?id=${idea.id}&table=${getTableName()}`);
  };
  const SCRIPT_PREVIEW_SENTENCES = 5;
  
  const getFirstNSentences = (text: string, n: number): string => {
    // Match sentences ending with ., !, or ?
    const sentenceRegex = /[^.!?]*[.!?]+/g;
    const sentences = text.match(sentenceRegex) || [];
    
    if (sentences.length <= n) {
      return text;
    }
    
    return sentences.slice(0, n).join('').trim();
  };
  
  const scriptNeedsTruncation = (text: string): boolean => {
    const sentenceRegex = /[^.!?]*[.!?]+/g;
    const sentences = text.match(sentenceRegex) || [];
    return sentences.length > SCRIPT_PREVIEW_SENTENCES;
  };
  const scriptSectionRef = useRef<HTMLDivElement>(null);

  // Scroll to top of card when expanded
  useEffect(() => {
    if (isExpandedProp && cardRef.current) {
      // Small delay to allow expansion animation to start
      setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 50);
    }
  }, [isExpandedProp]);
  const handleScriptToggle = () => {
    const wasExpanded = isScriptExpanded;
    setIsScriptExpanded(!isScriptExpanded);

    // When collapsing, scroll to show the full collapsed script section
    if (wasExpanded && scriptSectionRef.current) {
      setTimeout(() => {
        scriptSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 150);
    }
  };
  const [isScriptExpanded, setIsScriptExpanded] = useState(false);
  const [hookCarouselApi, setHookCarouselApi] = useState<CarouselApi>();
  const [overlayCarouselApi, setOverlayCarouselApi] = useState<CarouselApi>();
  const [insightsCarouselApi, setInsightsCarouselApi] = useState<CarouselApi>();
  const [improvementsCarouselApi, setImprovementsCarouselApi] = useState<CarouselApi>();
  const [hookCurrent, setHookCurrent] = useState(0);
  const [overlayCurrent, setOverlayCurrent] = useState(0);
  const [insightsCurrent, setInsightsCurrent] = useState(0);
  const [improvementsCurrent, setImprovementsCurrent] = useState(0);
  const {
    toggleFavorite,
    isLoading: isFavoriteLoading
  } = useFavorites();
  const {
    toggleUsed,
    isLoading: isUsedLoading
  } = useMarkAsUsed();
  React.useEffect(() => {
    if (!hookCarouselApi) return;
    setHookCurrent(hookCarouselApi.selectedScrollSnap());
    hookCarouselApi.on("select", () => {
      setHookCurrent(hookCarouselApi.selectedScrollSnap());
    });
  }, [hookCarouselApi]);
  React.useEffect(() => {
    if (!overlayCarouselApi) return;
    setOverlayCurrent(overlayCarouselApi.selectedScrollSnap());
    overlayCarouselApi.on("select", () => {
      setOverlayCurrent(overlayCarouselApi.selectedScrollSnap());
    });
  }, [overlayCarouselApi]);
  React.useEffect(() => {
    if (!insightsCarouselApi) return;
    setInsightsCurrent(insightsCarouselApi.selectedScrollSnap());
    insightsCarouselApi.on("select", () => {
      setInsightsCurrent(insightsCarouselApi.selectedScrollSnap());
    });
  }, [insightsCarouselApi]);
  React.useEffect(() => {
    if (!improvementsCarouselApi) return;
    setImprovementsCurrent(improvementsCarouselApi.selectedScrollSnap());
    improvementsCarouselApi.on("select", () => {
      setImprovementsCurrent(improvementsCarouselApi.selectedScrollSnap());
    });
  }, [improvementsCarouselApi]);
  const getViralScoreLevel = (score: number | null): ViralScoreLevel => {
    if (!score) return 'low';
    if (score >= 70) return 'high';
    if (score >= 40) return 'medium';
    return 'low';
  };
  const getViralScoreColor = (level: ViralScoreLevel) => {
    switch (level) {
      case 'high':
        return 'bg-success text-success-foreground border-success/20';
      case 'medium':
        return 'bg-warning text-warning-foreground border-warning/20';
      case 'low':
        return 'bg-muted text-muted-foreground border-muted/20';
    }
  };
  const getDynamicViralScoreColor = (score: number) => {
    const normalizedScore = Math.max(0, Math.min(100, score));
    const hue = normalizedScore / 100 * 120;
    return `hsl(${hue}, 70%, 45%)`;
  };
  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const created = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - created.getTime()) / 60000);
    if (diffInMinutes < 1) return 'Przed chwilą';
    if (diffInMinutes < 60) return `${diffInMinutes} min temu`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} godz. temu`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} dni temu`;
  };

  // Safely get array values from potentially non-array fields (handles Json type)
  const getArrayValue = (value: unknown): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(item => String(item));
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(item => String(item));
      } catch {
        return [value];
      }
    }
    return [];
  };

  // Parse improvements_applied field (can be string, array, or JSON)
  const parseImprovements = (improvements: unknown): string[] => {
    if (!improvements) return [];

    // If already an array
    if (Array.isArray(improvements)) {
      return improvements.map(imp => String(imp));
    }

    // If it's a string
    if (typeof improvements === 'string') {
      const trimmed = improvements.trim();

      // Try to parse as JSON array
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map(imp => String(imp));
          }
        } catch (e) {
          // If JSON parsing fails, treat as single string
          return [trimmed];
        }
      }

      // Single string improvement
      return [trimmed];
    }
    return [];
  };

  const parsePerplexityInsights = (data: unknown) => parseInsightsFromJson(data);

  const getCitationsFromInsights = (data: unknown): string[] => {
    if (!data) return [];
    let parsed = data;
    if (typeof data === 'string') {
      try { parsed = JSON.parse(data); } catch { return []; }
    }
    if (parsed && typeof parsed === 'object' && 'citations' in parsed) {
      const cits = (parsed as { citations: unknown }).citations;
      if (Array.isArray(cits)) return cits.map(String);
    }
    return [];
  };

  const altHooks = getArrayValue(idea.alt_hooks);
  const textOverlays = getArrayValue(idea.text_overlays);
  const parsedImprovements = parseImprovements(idea.improvements_applied);
  const perplexityInsights = parsePerplexityInsights(idea.perplexity_insights);
  const citations = getCitationsFromInsights(idea.perplexity_insights);

  // Parse content if it's JSON and extract script/content field for display
  const getScriptContent = (content: string | null): string => {
    if (!content) return 'No script content available';
    
    // Helper to normalize literal \n to actual newlines
    const normalizeNewlines = (text: string): string => {
      return text
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/\\n\\n/g, '\n\n')
        .replace(/\\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    };
    
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object' && parsed !== null) {
        const parsedObj = parsed as Record<string, unknown>;
        const script = parsedObj.script;
        const contentField = parsedObj.content;
        if (typeof script === 'string') return normalizeNewlines(script);
        if (typeof contentField === 'string') return normalizeNewlines(contentField);
      }
      return normalizeNewlines(content);
    } catch {
      return normalizeNewlines(content);
    }
  };
  const scriptContent = getScriptContent(idea.edited_content || idea.content || null);
  const viralLevel = getViralScoreLevel(idea.viral_score);
  const handleFavoriteToggle = async () => {
    const success = await toggleFavorite(idea.id, idea.is_favorited || false, idea.source);
    if (success && onFavoriteToggle) {
      onFavoriteToggle();
    }
  };
  const handleMarkAsUsed = async () => {
    const success = await toggleUsed(idea.id, idea.is_used || false, idea.source);
    if (success && onFavoriteToggle) {
      onFavoriteToggle();
    }
  };

  // Use shared citation URL resolver — imported from jsonTyping

  // Get post URL from source competitor data
  const getVideoUrl = () => {
    return idea.source_competitor?.url || `https://instagram.com/p/${idea.source_competitor?.username}/`;
  };
  if (isRowLayout) {
    return <Card ref={cardRef} className={`group bg-muted/40 border border-border/30 rounded-xl hover:bg-muted/50 transition-all duration-200 scroll-mt-20 ${className}`}>
        <CardHeader className="p-3 sm:p-4 md:p-5 cursor-pointer" onClick={() => onToggleExpand ? onToggleExpand() : setIsCardExpanded(!isCardExpanded)}>
          {/* Top row: Viral score (left) + Actions (right) */}
          <div className="flex items-center justify-between gap-3 mb-2">
            {/* Viral score - top left */}
            {idea.viral_score && <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border/50 shadow-sm">
                <TrendingUp className="h-4 w-4" style={{
              color: getDynamicViralScoreColor(idea.viral_score)
            }} />
                <span className="font-bold text-sm tabular-nums" style={{
              color: getDynamicViralScoreColor(idea.viral_score)
            }}>
                  {idea.viral_score.toFixed(2)}
                </span>
              </div>}
            {!idea.viral_score && (
              idea.source !== 'user_generated' ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/50 border border-border/30">
                  <TrendingUp className="h-4 w-4 text-muted-foreground/50" />
                  <span className="text-xs text-muted-foreground/70">Trwa obliczanie</span>
                </div>
              ) : <div />
            )}

            {/* Right: Actions */}
            <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
              {/* Timestamp - hidden on mobile */}
              <span className="hidden md:block text-xs text-muted-foreground whitespace-nowrap mr-2">
                {formatTimeAgo(idea.created_at)}
              </span>
              
              <Button variant="ghost" size="sm" onClick={handleFavoriteToggle} disabled={isFavoriteLoading} aria-label={idea.is_favorited ? "Usuń z ulubionych" : "Dodaj do ulubionych"} className={`h-9 w-9 sm:h-8 sm:w-8 p-0 rounded-full hover:scale-110 transition-transform ${idea.is_favorited ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-950" : "text-muted-foreground hover:bg-muted"}`}>
                <Heart className={`h-4 w-4 ${idea.is_favorited ? "fill-red-500" : ""}`} />
              </Button>
              {!isTrashView && <Button variant="ghost" size="sm" onClick={handleMarkAsUsed} disabled={isUsedLoading} aria-label={idea.is_used ? "Oznacz jako nieużyte" : "Oznacz jako użyte"} className={`h-9 w-9 sm:h-8 sm:w-8 p-0 rounded-full hover:scale-110 transition-transform ${idea.is_used ? "text-green-600 dark:text-green-400" : "text-muted-foreground hover:bg-muted"}`}>
                  <CheckCircle className={`h-4 w-4 ${idea.is_used ? "fill-green-100 dark:fill-green-900" : ""}`} />
                </Button>}

              {/* Three-dot menu */}
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={e => e.stopPropagation()} aria-label="Więcej opcji" className="h-9 w-9 sm:h-8 sm:w-8 p-0 rounded-full hover:scale-110 transition-transform text-muted-foreground hover:bg-muted">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background border border-border shadow-lg z-50" onClick={e => e.stopPropagation()}>
                  {!isTrashView && (
                    <>
                      <DropdownMenuItem onClick={handleNavigateToEditor} className="cursor-pointer">
                        <FileEdit className="mr-2 h-4 w-4" />
                        <span>Edytuj</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleNavigateToPrompter} className="cursor-pointer">
                        <MonitorPlay className="mr-2 h-4 w-4" />
                        <span>Otwórz w prompterze</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {!isTrashView ? <DropdownMenuItem onClick={e => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  if (onTrashAction) {
                    setTimeout(() => {
                      onTrashAction(idea.id, idea.source || 'scraped');
                    }, 50);
                  }
                }} className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-950">
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Przenieś do kosza</span>
                    </DropdownMenuItem> : <DropdownMenuItem onClick={e => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  if (onTrashAction) {
                    setTimeout(() => {
                      onTrashAction(idea.id, idea.source || 'scraped');
                    }, 50);
                  }
                }} className="cursor-pointer text-green-600 dark:text-green-400 focus:text-green-600 dark:focus:text-green-400 focus:bg-green-50 dark:focus:bg-green-950">
                      <Undo className="mr-2 h-4 w-4" />
                      <span>Przywróć z kosza</span>
                    </DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Title and description - full width */}
          <div className="w-full">
            {idea.main_idea && (
              <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Lightbulb className="h-3 w-3 flex-shrink-0" />
                <span className="line-clamp-1">{idea.main_idea}</span>
              </p>
            )}
            <h3 className="font-semibold text-base sm:text-lg leading-snug text-foreground mb-1">
              {idea.title}
            </h3>

            {idea.description && <p className="text-sm text-muted-foreground line-clamp-2">
                {idea.description}
              </p>}
          </div>

          {/* Expand button - centered at bottom */}
          {!(isExpandedProp !== undefined ? isExpandedProp : isCardExpanded) && <div className="flex justify-center mt-3 pt-2 border-t border-border/30">
              <Button variant="outline" size="sm" className="text-xs border-primary/30 text-primary hover:bg-primary/10 gap-1">
                <span>Rozwiń</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>}
        </CardHeader>

        {/* Content Section - Split Layout (Collapsible) */}
        {(isExpandedProp !== undefined ? isExpandedProp : isCardExpanded) && idea.content && <CardContent className="pt-0 pb-3 sm:pb-4 px-3 sm:px-4 animate-accordion-down">
            <Separator className="mb-3 sm:mb-4" />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              {/* 1. Hook Section */}
              {altHooks.length > 0 && <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg sm:text-xl">🎣</span>
                    <h3 className="text-sm sm:text-base font-bold text-foreground">Hook</h3>
                  </div>
                  
                  <Carousel setApi={setHookCarouselApi} opts={{
              watchDrag: false
            }} className="w-full mb-3">
                    <CarouselContent>
                      {altHooks.map((hook, index) => <CarouselItem key={index}>
                          <div className="p-3 sm:p-4 rounded-lg bg-background/80 border border-blue-500/10">
                            <p className="text-sm sm:text-xs text-foreground leading-relaxed">{hook}</p>
                          </div>
                        </CarouselItem>)}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-blue-500/10 border-blue-500/30 hover:bg-blue-500 hover:text-white disabled:opacity-30 p-0" onClick={() => hookCarouselApi?.scrollPrev()} disabled={hookCurrent === 0}>
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold min-w-[2.5rem] text-center bg-blue-500/10 px-2 py-0.5 rounded-full">
                      {hookCurrent + 1}/{altHooks.length}
                    </span>
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-blue-500/10 border-blue-500/30 hover:bg-blue-500 hover:text-white disabled:opacity-30 p-0" onClick={() => hookCarouselApi?.scrollNext()} disabled={hookCurrent === altHooks.length - 1}>
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>}

              {/* 2. Text Overlays Section */}
              {textOverlays.length > 0 && <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg sm:text-xl">📝</span>
                    <h3 className="text-sm sm:text-base font-bold text-foreground">Nakładki tekstowe</h3>
                  </div>
                  
                  <Carousel setApi={setOverlayCarouselApi} opts={{
              watchDrag: false
            }} className="w-full mb-3">
                    <CarouselContent>
                      {textOverlays.map((overlay, index) => <CarouselItem key={index}>
                          <div className="p-3 sm:p-4 rounded-lg bg-background/80 border border-orange-500/10">
                            <p className="text-sm sm:text-xs text-foreground leading-relaxed">{overlay}</p>
                          </div>
                        </CarouselItem>)}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-orange-500/10 border-orange-500/30 hover:bg-orange-500 hover:text-white disabled:opacity-30 p-0" onClick={() => overlayCarouselApi?.scrollPrev()} disabled={overlayCurrent === 0}>
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <span className="text-xs text-orange-600 dark:text-orange-400 font-semibold min-w-[2.5rem] text-center bg-orange-500/10 px-2 py-0.5 rounded-full">
                      {overlayCurrent + 1}/{textOverlays.length}
                    </span>
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-orange-500/10 border-orange-500/30 hover:bg-orange-500 hover:text-white disabled:opacity-30 p-0" onClick={() => overlayCarouselApi?.scrollNext()} disabled={overlayCurrent === textOverlays.length - 1}>
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>}

              <div ref={scriptSectionRef} className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 lg:col-span-2 overflow-hidden">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg sm:text-xl">🎬</span>
                    <h3 className="text-sm sm:text-base font-bold text-foreground">Scenariusz</h3>
                  </div>
                  {/* Desktop buttons - inline with title */}
                  <div className="hidden sm:flex items-center gap-2">
                    <Button onClick={() => onCopyScript(scriptContent)} className="px-3 h-8 text-xs border-border/50 hover:bg-muted" variant="outline" size="sm">
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      Skopiuj
                    </Button>
                    <Button onClick={handleNavigateToEditor} className="px-3 h-8 text-xs border-border/50 hover:bg-muted" variant="outline" size="sm">
                      <FileEdit className="h-3.5 w-3.5 mr-1.5" />
                      Edytuj
                    </Button>
                    <Button onClick={handleNavigateToPrompter} className="px-3 h-8 text-xs bg-primary/10 border-primary/30 text-primary hover:bg-primary/20" variant="outline" size="sm">
                      <MonitorPlay className="h-3.5 w-3.5 mr-1.5" />
                      Prompter
                    </Button>
                  </div>
                </div>
                {/* Mobile buttons - below title */}
                <div className="flex sm:hidden items-center gap-1 mb-3 w-full">
                  <Button onClick={() => onCopyScript(scriptContent)} className="flex-1 min-w-0 px-1.5 h-7 text-[10px] border-border/50 hover:bg-muted" variant="outline" size="sm">
                    <Copy className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">Skopiuj</span>
                  </Button>
                  <Button onClick={handleNavigateToEditor} className="flex-1 min-w-0 px-1.5 h-7 text-[10px] border-border/50 hover:bg-muted" variant="outline" size="sm">
                    <FileEdit className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">Edytuj</span>
                  </Button>
                  <Button onClick={handleNavigateToPrompter} className="flex-1 min-w-0 px-1.5 h-7 text-[10px] bg-primary/10 border-primary/30 text-primary hover:bg-primary/20" variant="outline" size="sm">
                    <MonitorPlay className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">Prompter</span>
                  </Button>
                </div>
                
                <div className="p-3 sm:p-4 rounded-lg bg-background border border-border">
                  {isScriptExpanded ? <p className="text-sm sm:text-base leading-relaxed sm:leading-loose whitespace-pre-wrap text-foreground">
                      {scriptContent}
                    </p> : <div className="relative">
                      <p className="text-sm sm:text-base leading-relaxed sm:leading-loose whitespace-pre-wrap text-foreground">
                        {getFirstNSentences(scriptContent, SCRIPT_PREVIEW_SENTENCES)}
                      </p>
                      {/* Gradient fade overlay */}
                      {scriptNeedsTruncation(scriptContent) && (
                        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                      )}
                    </div>}
                </div>

                {scriptNeedsTruncation(scriptContent) && (
                  <div className="flex gap-2 mt-3">
                    <Button onClick={handleScriptToggle} className="flex-1 bg-primary hover:bg-primary/90 h-8 text-xs" size="sm">
                      {isScriptExpanded ? <>
                          <ChevronUp className="mr-1.5 h-3 w-3" />
                          Zwiń
                        </> : <>
                          <ChevronDown className="mr-1.5 h-3 w-3" />
                          Rozwiń
                        </>}
                    </Button>
                  </div>
                )}
              </div>

              {/* 4. Perplexity Insights (Wróżbita) */}
              {perplexityInsights.length > 0 && <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg sm:text-xl">💡</span>
                    <h3 className="text-sm sm:text-base font-bold text-foreground">Ciekawostki</h3>
                  </div>
                  
                  <Carousel setApi={setInsightsCarouselApi} opts={{
              watchDrag: false
            }} className="w-full mb-3">
                    <CarouselContent>
                      {perplexityInsights.map((insight, index) => {
                  const citationUrl = getCitationUrl(insight.source, { citations });
                  return <CarouselItem key={index}>
                            <div className="p-3 sm:p-4 rounded-lg bg-background/60 border border-emerald-500/10">
                              <InsightItemDisplay insight={insight} fallbackCitationUrl={citationUrl} compact />
                            </div>
                          </CarouselItem>;
                })}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500 hover:text-white disabled:opacity-30 p-0" onClick={() => insightsCarouselApi?.scrollPrev()} disabled={insightsCurrent === 0}>
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold min-w-[2.5rem] text-center bg-emerald-500/10 px-2 py-0.5 rounded-full">
                      {insightsCurrent + 1}/{perplexityInsights.length}
                    </span>
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500 hover:text-white disabled:opacity-30 p-0" onClick={() => insightsCarouselApi?.scrollNext()} disabled={insightsCurrent === perplexityInsights.length - 1}>
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>}

              {/* 5. Improvements Applied (Ulepszenia) */}
              {parsedImprovements.length > 0 && <div className="p-3 sm:p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-blue-500" />
                    <h3 className="text-sm sm:text-base font-bold text-foreground">Zastosowane ulepszenia</h3>
                  </div>
                  
                  <Carousel setApi={setImprovementsCarouselApi} opts={{
              watchDrag: false
            }} className="w-full mb-3">
                    <CarouselContent>
                      {parsedImprovements.map((improvement, index) => <CarouselItem key={index}>
                          <div className="p-3 sm:p-4 rounded-lg bg-background/80 border border-blue-500/10 flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed">{improvement}</p>
                          </div>
                        </CarouselItem>)}
                    </CarouselContent>
                  </Carousel>

                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-blue-500/10 border-blue-500/30 hover:bg-blue-500 hover:text-white disabled:opacity-30 p-0" onClick={() => improvementsCarouselApi?.scrollPrev()} disabled={improvementsCurrent === 0}>
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold min-w-[2.5rem] text-center bg-blue-500/10 px-2 py-0.5 rounded-full">
                      {improvementsCurrent + 1}/{parsedImprovements.length}
                    </span>
                    <Button variant="outline" size="sm" className="h-7 w-7 rounded-full bg-blue-500/10 border-blue-500/30 hover:bg-blue-500 hover:text-white disabled:opacity-30 p-0" onClick={() => improvementsCarouselApi?.scrollNext()} disabled={improvementsCurrent === parsedImprovements.length - 1}>
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>}

            </div>
          </CardContent>}
      </Card>;
  }
  return <Card className={`group glass-card hover:shadow-elevated transition-all duration-300 border-l-4 border-l-primary/20 hover:border-l-primary hover:scale-[1.02] ${className}`}>
      <CardHeader className="pb-3 space-y-3">
        {/* Main idea (user input) + Title */}
        {idea.main_idea && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Lightbulb className="h-3 w-3 flex-shrink-0" />
            <span className="line-clamp-1">{idea.main_idea}</span>
          </p>
        )}
        <h3 className="font-bold text-xl md:text-2xl leading-tight line-clamp-3 group-hover:text-primary transition-colors">
          {idea.title}
        </h3>

        {/* Metadata - Compact */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {idea.viral_score ? (
              <Badge className={`${getViralScoreColor(viralLevel)} flex items-center gap-1 border text-xs`} variant="outline">
                <Star className="h-3 w-3" />
                <span className="tabular-nums">{idea.viral_score}</span>
              </Badge>
            ) : idea.source !== 'user_generated' ? (
              <Badge className="bg-muted/50 text-muted-foreground/70 flex items-center gap-1 border border-border/30 text-xs" variant="outline">
                <Star className="h-3 w-3 opacity-50" />
                <span>Trwa obliczanie</span>
              </Badge>
            ) : null}
            {idea.is_used && <Badge variant="secondary" className="bg-pink-500 text-white flex items-center gap-1 text-xs">
                <Check className="h-3 w-3" />
                Used
              </Badge>}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatTimeAgo(idea.created_at)}
          </span>
        </div>

        {/* Description */}
        {idea.description && <p className="text-sm text-muted-foreground line-clamp-2">
            {idea.description}
          </p>}
      </CardHeader>

      {/* Content Section */}
      {idea.content && <CardContent className="pt-0 space-y-4">
          <Separator className="mb-1" />
          
          {/* Budowa Rolki Section */}
          {(altHooks.length > 0 || textOverlays.length > 0) && <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-4 w-1 bg-gradient-to-b from-primary to-primary/50 rounded-full"></div>
                <span className="text-sm font-semibold text-foreground">Budowa Rolki</span>
              </div>

              {/* Hook Section */}
              {altHooks.length > 0 && <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold">
                      1
                    </div>
                    <span className="text-lg">🎣</span>
                    <h3 className="text-sm font-bold text-foreground">Hook</h3>
                    <span className="text-xs text-muted-foreground">Przyciągnij uwagę</span>
                  </div>
                  
                  <Carousel setApi={setHookCarouselApi} opts={{
            watchDrag: false
          }} className="w-full mb-3">
                    <CarouselContent>
                      {altHooks.map((hook, index) => <CarouselItem key={index}>
                          <div className="p-3 rounded-lg bg-background/80 border border-blue-500/10">
                            <p className="text-sm text-foreground leading-relaxed">{hook}</p>
                          </div>
                        </CarouselItem>)}
                    </CarouselContent>
                  </Carousel>

                  {/* Navigation Controls */}
                  <div className="flex items-center justify-center gap-3">
                    <Button variant="outline" size="sm" className="h-9 w-9 rounded-full bg-blue-500/10 border-blue-500/30 hover:bg-blue-500 hover:text-white disabled:opacity-30" onClick={() => hookCarouselApi?.scrollPrev()} disabled={hookCurrent === 0}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold min-w-[3rem] text-center bg-blue-500/10 px-3 py-1 rounded-full">
                      {hookCurrent + 1} / {altHooks.length}
                    </span>
                    <Button variant="outline" size="sm" className="h-9 w-9 rounded-full bg-blue-500/10 border-blue-500/30 hover:bg-blue-500 hover:text-white disabled:opacity-30" onClick={() => hookCarouselApi?.scrollNext()} disabled={hookCurrent === altHooks.length - 1}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>}

              {/* Scenariusz Section */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">
                    2
                  </div>
                  <span className="text-lg">🎬</span>
                  <h3 className="text-sm font-bold text-foreground">Scenariusz</h3>
                </div>
                
                <div className={`p-3 rounded-lg bg-background border border-border transition-all duration-300 ${isScriptExpanded ? 'pb-4' : ''}`}>
                  <div className={`w-full transition-all duration-300 ${isScriptExpanded ? 'max-h-[600px] overflow-y-auto pr-2' : 'max-h-24 overflow-hidden'}`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground">
                      {scriptContent}
                    </p>
                  </div>
                  
                  {/* Gradient overlay when collapsed */}
                  {!isScriptExpanded && <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none" />}
                </div>

                <div className="flex gap-2 mt-3">
                  <Button onClick={() => onCopyScript(scriptContent)} className="flex-1 bg-primary hover:bg-primary/90 h-8 text-xs" size="sm">
                    <Copy className="mr-1.5 h-3 w-3" />
                    Skopiuj
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setIsScriptExpanded(!isScriptExpanded)} className="px-3 h-8 text-xs border-primary/30 text-primary hover:bg-primary/10">
                    {isScriptExpanded ? <>
                        <ChevronUp className="mr-1 h-3 w-3" />
                        Zwiń
                      </> : <>
                        <ChevronDown className="mr-1 h-3 w-3" />
                        Rozwiń
                      </>}
                  </Button>
                </div>
              </div>

              {/* Text Overlays Section */}
              {textOverlays.length > 0 && <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold">
                      3
                    </div>
                    <span className="text-lg">📝</span>
                    <h3 className="text-sm font-bold text-foreground">Nakładki tekstowe</h3>
                    <span className="text-xs text-muted-foreground">Teksty na wideo</span>
                  </div>
                  
                  <Carousel setApi={setOverlayCarouselApi} opts={{
            watchDrag: false
          }} className="w-full mb-3">
                    <CarouselContent>
                      {textOverlays.map((overlay, index) => <CarouselItem key={index}>
                          <div className="p-3 rounded-lg bg-background/80 border border-orange-500/10">
                            <p className="text-sm text-foreground leading-relaxed">{overlay}</p>
                          </div>
                        </CarouselItem>)}
                    </CarouselContent>
                  </Carousel>

                  {/* Navigation Controls */}
                  <div className="flex items-center justify-center gap-3">
                    <Button variant="outline" size="sm" className="h-9 w-9 rounded-full bg-orange-500/10 border-orange-500/30 hover:bg-orange-500 hover:text-white disabled:opacity-30" onClick={() => overlayCarouselApi?.scrollPrev()} disabled={overlayCurrent === 0}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-orange-600 dark:text-orange-400 font-semibold min-w-[3rem] text-center bg-orange-500/10 px-3 py-1 rounded-full">
                      {overlayCurrent + 1} / {textOverlays.length}
                    </span>
                    <Button variant="outline" size="sm" className="h-9 w-9 rounded-full bg-orange-500/10 border-orange-500/30 hover:bg-orange-500 hover:text-white disabled:opacity-30" onClick={() => overlayCarouselApi?.scrollNext()} disabled={overlayCurrent === textOverlays.length - 1}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>}
            </div>}

          {/* Perplexity Insights */}
          {perplexityInsights.length > 0 && <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">💡</span>
                <h3 className="text-sm font-bold text-foreground">Ciekawostki:</h3>
                <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-0">
                  AI Research
                </Badge>
              </div>
              
              <Carousel setApi={setInsightsCarouselApi} opts={{
          watchDrag: false
        }} className="w-full mb-3">
                <CarouselContent>
                  {perplexityInsights.map((insight, index) => {
              const citationUrl = getCitationUrl(insight.source, { citations });
              return <CarouselItem key={index}>
                        <div className="p-3 rounded-lg bg-background/60 border border-emerald-500/10">
                          <InsightItemDisplay insight={insight} fallbackCitationUrl={citationUrl} />
                        </div>
                      </CarouselItem>;
            })}
                </CarouselContent>
              </Carousel>

              {/* Navigation Controls */}
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" size="sm" className="h-9 w-9 rounded-full bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500 hover:text-white disabled:opacity-30" onClick={() => insightsCarouselApi?.scrollPrev()} disabled={insightsCurrent === 0}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold min-w-[3rem] text-center bg-emerald-500/10 px-3 py-1 rounded-full">
                  {insightsCurrent + 1} / {perplexityInsights.length}
                </span>
                <Button variant="outline" size="sm" className="h-9 w-9 rounded-full bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500 hover:text-white disabled:opacity-30" onClick={() => insightsCarouselApi?.scrollNext()} disabled={insightsCurrent === perplexityInsights.length - 1}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>}

          {/* Improvements Applied */}
          {parsedImprovements.length > 0 && <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">✨</span>
                <h3 className="text-sm font-bold text-foreground">Zastosowane ulepszenia</h3>
              </div>
              
              <div className="space-y-2">
                {parsedImprovements.map((improvement, index) => <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground leading-relaxed">{improvement}</p>
                  </div>)}
              </div>
            </div>}


          {/* Action Buttons - Compact */}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleFavoriteToggle} disabled={isFavoriteLoading} aria-label={idea.is_favorited ? "Usuń z ulubionych" : "Dodaj do ulubionych"} className={`h-8 w-8 p-0 ${idea.is_favorited ? "text-red-500 hover:text-red-600" : ""}`}>
              <Heart className={`h-3.5 w-3.5 ${idea.is_favorited ? "fill-red-500" : ""}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleMarkAsUsed} disabled={isUsedLoading} aria-label={idea.is_used ? "Oznacz jako nieużyte" : "Oznacz jako użyte"} className={`h-8 w-8 p-0 transition-all ${idea.is_used ? "text-green-600 dark:text-green-400" : "hover:bg-muted"}`}>
              <CheckCircle className={`h-3.5 w-3.5 ${idea.is_used ? "fill-green-100 dark:fill-green-900" : ""}`} />
            </Button>
          </div>
        </CardContent>}
    </Card>;
};